import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import type { WhatsAppTemplate } from '../../api/schema'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

/**
 * The approved WhatsApp templates a reply can use (spec 2026-10-07-02).
 *
 * Once Meta's 24 h window has closed only an approved template reaches the
 * resident, so the composer swaps its text box for this: pick a
 * `(name, language)` pair, fill one input per `{{n}}`, and read the rendered
 * text before sending. Templates in the organization's language come first.
 * The selection is plain state owned by the composer, never the autosaved
 * draft.
 */

/** A template's key in the select: name and language together. */
export function templateKey(tpl: Pick<WhatsAppTemplate, 'name' | 'language'>): string {
    return `${tpl.name}|${tpl.language}`
}

/** Substitutes `{{n}}` with `params[n-1]`; an empty value keeps its placeholder. */
export function renderTemplate(body: string, params: string[]): string {
    return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n: string) => {
        const value = params[Number(n) - 1]

        return value && value.trim() !== '' ? value : match
    })
}

/**
 * The continue template whose single variable is the resident's page URL: the
 * server fills it, so the manager types nothing (spec 2026-10-26-05).
 */
export function isLinkParamTemplate(tpl: Pick<WhatsAppTemplate, 'name' | 'param_count'>, continueTemplate?: string): boolean {
    return continueTemplate !== undefined && tpl.name === continueTemplate && tpl.param_count === 1
}

/** How many values the manager fills in: none for the server-filled link. */
export function manualParamCount(tpl: Pick<WhatsAppTemplate, 'name' | 'param_count'>, continueTemplate?: string): number {
    return isLinkParamTemplate(tpl, continueTemplate) ? 0 : tpl.param_count
}

/** A template whose text speaks of a link but that has no button opening the page. */
export function mentionsMissingLink(tpl: Pick<WhatsAppTemplate, 'name' | 'param_count' | 'body' | 'has_url_button'>, continueTemplate?: string): boolean {
    return !tpl.has_url_button && !isLinkParamTemplate(tpl, continueTemplate) && /\b(lien|link)\b/i.test(tpl.body)
}

export function useWhatsAppTemplates(org: string, enabled: boolean) {
    return $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations/whatsapp-templates',
        { params: { path: { orgCode: org } } },
        { enabled, staleTime: 5 * 60 * 1000, retry: false },
    )
}

export function WhatsAppTemplatePicker({
    templates,
    defaultLanguage,
    warning,
    continueTemplate,
    loading,
    selected,
    onSelect,
    params,
    onParamsChange,
    disabled,
}: {
    templates: WhatsAppTemplate[]
    defaultLanguage?: string
    warning?: string
    continueTemplate?: string
    loading: boolean
    selected: WhatsAppTemplate | null
    onSelect: (tpl: WhatsAppTemplate | null) => void
    params: string[]
    onParamsChange: (params: string[]) => void
    disabled?: boolean
}) {
    const { t } = useTranslation('conversations')

    // The organization's language first, then by name: the list a French
    // manager scans starts with the French templates.
    const ordered = useMemo(() => {
        const lang = (defaultLanguage ?? '').toLowerCase()
        const rank = (tpl: WhatsAppTemplate) => {
            const l = tpl.language.toLowerCase()

            return lang && (l === lang || l.startsWith(`${lang}_`)) ? 0 : 1
        }

        return [...templates].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name) || a.language.localeCompare(b.language))
    }, [templates, defaultLanguage])

    return (
        <div className="space-y-2" data-testid="conversation-template-picker">
            <Select
                value={selected ? templateKey(selected) : ''}
                onValueChange={(key) => {
                    const tpl = ordered.find((x) => templateKey(x) === key) ?? null
                    onSelect(tpl)
                    onParamsChange(Array.from({ length: tpl ? manualParamCount(tpl, continueTemplate) : 0 }, () => ''))
                }}
                disabled={disabled || loading}
            >
                <SelectTrigger className="h-auto min-h-8 py-1 text-sm text-left" aria-label={t('template.choose')} data-testid="conversation-template-select">
                    <SelectValue placeholder={loading ? t('template.loading') : t('template.choose')} />
                </SelectTrigger>
                <SelectContent>
                    {ordered.map((tpl) => (
                        <SelectItem
                            key={templateKey(tpl)}
                            value={templateKey(tpl)}
                            data-testid={`conversation-template-option-${tpl.name}-${tpl.language}`}
                        >
                            <span className="flex flex-col items-start gap-0.5 max-w-[28rem]">
                                <span className="flex items-center gap-1.5">
                                    <span className="font-medium">{tpl.name}</span>
                                    <span className="text-muted-foreground">· {tpl.language}</span>
                                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                                        {tpl.category}
                                    </Badge>
                                </span>
                                <span className="text-xs text-muted-foreground line-clamp-1">{tpl.body}</span>
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {warning && (
                <p className="text-xs text-muted-foreground" data-testid="conversation-template-warning">
                    {t('template.catalogue_partial')}
                </p>
            )}
            {selected && manualParamCount(selected, continueTemplate) > 0 && (
                <div className="grid gap-2 sm:grid-cols-2">
                    {Array.from({ length: manualParamCount(selected, continueTemplate) }, (_, i) => (
                        <Input
                            key={i}
                            value={params[i] ?? ''}
                            onChange={(e) => {
                                const next = [...params]
                                next[i] = e.target.value
                                onParamsChange(next)
                            }}
                            placeholder={t('template.param', { n: i + 1 })}
                            aria-label={t('template.param', { n: i + 1 })}
                            className="h-8 text-sm"
                            disabled={disabled}
                            data-testid={`conversation-template-param-${i + 1}`}
                        />
                    ))}
                </div>
            )}
            {selected && (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm whitespace-pre-wrap" data-testid="conversation-template-preview">
                    <span className="block text-xs text-muted-foreground mb-1">{t('template.preview')}</span>
                    {renderTemplate(selected.body, isLinkParamTemplate(selected, continueTemplate) ? [t('template.link_placeholder')] : params)}
                    {selected.has_url_button && (
                        <span className="block text-xs text-muted-foreground mt-1">{t('template.url_button')}</span>
                    )}
                </div>
            )}
            {selected && mentionsMissingLink(selected, continueTemplate) && (
                <p className="text-xs text-destructive" data-testid="conversation-template-no-link-warning">
                    {t('template.no_link_warning')}
                </p>
            )}
        </div>
    )
}
